import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAgency } from './AgencyContext';

/**
 * Global "active brand" context.
 *
 * Purpose: let any authenticated screen (Dashboard, LIVE Ops, Reports,
 * Financials, Calendar) refilter itself by the brand the user is currently
 * focused on WITHOUT navigating away. The active brand is:
 *
 *   1. `?brand=<id>` in the URL (wins if present so links are shareable).
 *   2. Otherwise, the last brand the user picked (persisted in localStorage).
 *   3. Otherwise, `null` = "All brands".
 *
 * The switcher in the header writes to both.
 */

const LS_KEY = 'ftos:activeBrandId:v1';

export interface ActiveBrand {
  id: string;
  name: string;
  logoUrl: string | null;
}

interface BrandContextValue {
  brandId: string | null;
  brand: ActiveBrand | null;
  brands: ActiveBrand[];
  loading: boolean;
  setBrand: (id: string | null) => void;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function BrandProvider({ children }: { children: ReactNode }) {
  const { agencyId, isSuperAdmin, loading: agencyLoading } = useAgency();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBrand = searchParams.get('brand');

  const [stored, setStored] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_KEY); } catch { return null; }
  });

  const brandId = urlBrand ?? stored;

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['brand-switcher-list', agencyId, isSuperAdmin],
    enabled: !agencyLoading && (!!agencyId || isSuperAdmin),
    staleTime: 60_000,
    queryFn: async (): Promise<ActiveBrand[]> => {
      let q = supabase
        .from('brands')
        .select('id, name, logo_url, status')
        .is('deleted_at', null)
        .in('status', ['active', 'onboarding', 'paused'])
        .order('name');
      if (!isSuperAdmin && agencyId) q = q.eq('agency_id', agencyId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((b: any) => ({ id: b.id, name: b.name, logoUrl: b.logo_url }));
    },
  });

  const brand = useMemo(
    () => (brandId ? brands.find((b) => b.id === brandId) ?? null : null),
    [brandId, brands],
  );

  const setBrand = useCallback((id: string | null) => {
    try {
      if (id) localStorage.setItem(LS_KEY, id);
      else localStorage.removeItem(LS_KEY);
    } catch { /* ignore */ }
    setStored(id);
    // Only touch the URL if it already had a ?brand param — avoids polluting
    // deep-link URLs like /brands/:id where the id in the path already scopes.
    if (urlBrand !== null) {
      const next = new URLSearchParams(searchParams);
      if (id) next.set('brand', id);
      else next.delete('brand');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, urlBrand]);

  // If URL brand is set on mount, sync it into storage.
  useEffect(() => {
    if (urlBrand && urlBrand !== stored) {
      try { localStorage.setItem(LS_KEY, urlBrand); } catch { /* noop */ }
      setStored(urlBrand);
    }
  }, [urlBrand]); // eslint-disable-line

  const value: BrandContextValue = {
    brandId: brand?.id ?? null,
    brand,
    brands,
    loading: isLoading,
    setBrand,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useActiveBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useActiveBrand must be used within <BrandProvider>');
  return ctx;
}
